<template>
  <div class="v2-unassign-step">
    <div
      v-if="isLoading"
      class="v2-flex v2-justify-center v2-py-4"
    >
      <v2-spinner
        size="lg"
        label="Loading shipments..."
      />
    </div>

    <p
      v-else-if="!shipments.length"
      class="v2-unassign-step__empty"
    >
      None of the selected products are on a shipment.
    </p>

    <template v-else>
      <div class="v2-flex v2-justify-between v2-items-center v2-mb-2">
        <label class="v2-unassign-step__select-all">
          <input
            type="checkbox"
            :checked="allSelected"
            @change="toggleAll($event.target.checked)"
          >
          <span>Select all</span>
        </label>
        <span class="v2-unassign-step__count">
          {{ localSelectedIds.length }} of {{ removableShipments.length }} selected
        </span>
      </div>

      <label
        v-for="shipment in shipments"
        :key="shipment.shipmentId"
        class="v2-unassign-step__row"
        :class="{
          'v2-unassign-step__row--selected': localSelectedIds.includes(shipment.shipmentId),
          'v2-unassign-step__row--disabled': !isRemovable(shipment),
        }"
      >
        <input
          type="checkbox"
          :checked="localSelectedIds.includes(shipment.shipmentId)"
          :disabled="!isRemovable(shipment)"
          @change="toggleShipment(shipment.shipmentId)"
        >
        <span class="v2-unassign-step__row-main">
          <span class="v2-unassign-step__row-title">
            {{ shipment.shipmentSeqId }}
          </span>
          <span class="v2-unassign-step__row-sub">
            {{ shipment.retailerName }}
          </span>
        </span>
        <!-- The picker lists products on shipments of any status, so say up
             front which ones cannot be changed rather than letting the user
             pick one and read it off a partial-success message. -->
        <v2-badge
          v-if="!isRemovable(shipment)"
          variant="secondary"
        >
          {{ statusLabel(shipment) }}
        </v2-badge>
        <v2-badge
          v-else-if="wouldEmpty(shipment)"
          variant="warning"
        >
          Shipment will be deleted
        </v2-badge>
      </label>
    </template>
  </div>
</template>

<script>
import { constants } from '@kingpin-global/kingpin-utils-frontend'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Badge from '@/components/v2/V2Badge.vue'

const { SHIPMENT_STATUS } = constants

// Mirrors the server-side guard: only a still-created shipment can lose
// products. Anything else is refused, so the step disables it up front.
const STATUS_LABELS = {
  [SHIPMENT_STATUS.SHIPMENT_IN_TRANSIT]: 'In transit',
  [SHIPMENT_STATUS.SHIPMENT_DELIVERED]: 'Delivered',
  [SHIPMENT_STATUS.CANCELLED]: 'Cancelled',
  [SHIPMENT_STATUS.READY_FOR_COLLECTION]: 'Ready for collection',
}

export default {
  name: 'V2UnassignShipmentStep',
  components: { V2Spinner, V2Badge },
  props: {
    // Rows from the returnable-products endpoint, already narrowed to the
    // products picked in the previous step.
    selectedProducts: { type: Array, default: () => [] },
    selectedShipmentIds: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
  },
  emits: ['update'],
  data() {
    return {
      localSelectedIds: [...this.selectedShipmentIds],
    }
  },
  computed: {
    // Only shipments that actually hold one of the picked products, so the step
    // never offers a shipment this action would not touch.
    shipments() {
      const byId = {}
      this.selectedProducts.forEach(product => {
        product.shipments.forEach(shipment => {
          byId[shipment.shipmentId] = shipment
        })
      })
      return Object.values(byId)
    },
    // Select all applies only to the ones that can actually be changed, so it
    // never puts a shipment in the payload the server is going to refuse.
    removableShipments() {
      return this.shipments.filter(shipment => this.isRemovable(shipment))
    },
    allSelected() {
      return (
        this.removableShipments.length > 0
        && this.removableShipments.every(
          shipment => this.localSelectedIds.includes(shipment.shipmentId),
        )
      )
    },
  },
  watch: {
    // Going back and changing the product picks can drop shipments from the
    // candidate list; anything no longer offered must not stay selected.
    shipments() {
      const availableIds = this.shipments.map(shipment => shipment.shipmentId)
      const pruned = this.localSelectedIds.filter(id => availableIds.includes(id))
      if (pruned.length !== this.localSelectedIds.length) {
        this.localSelectedIds = pruned
        this.$emit('update', [...this.localSelectedIds])
      }
    },
  },
  methods: {
    // A shipment that has left cannot lose products; the server refuses it.
    // Older API responses carry no status, so absence means "assume removable"
    // and let the server be the authority.
    isRemovable(shipment) {
      return (
        !shipment.status
        || shipment.status === SHIPMENT_STATUS.SHIPMENT_CREATED
      )
    },
    statusLabel(shipment) {
      return STATUS_LABELS[shipment.status] || 'Cannot be changed'
    },
    // Every product this shipment holds is being removed, so it will be emptied
    // and therefore deleted.
    wouldEmpty(shipment) {
      const selectedOnThisShipment = this.selectedProducts.filter(product => product.shipments.some(
        ref => ref.shipmentId === shipment.shipmentId,
      )).length
      return selectedOnThisShipment >= shipment.productCount
    },
    toggleShipment(shipmentId) {
      const idx = this.localSelectedIds.indexOf(shipmentId)
      if (idx >= 0) {
        this.localSelectedIds.splice(idx, 1)
      } else {
        this.localSelectedIds.push(shipmentId)
      }
      this.$emit('update', [...this.localSelectedIds])
    },
    toggleAll(checked) {
      this.localSelectedIds = checked
        ? this.removableShipments.map(shipment => shipment.shipmentId)
        : []
      this.$emit('update', [...this.localSelectedIds])
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-unassign-step__row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.5rem;
  border-radius: 0.5rem;
  cursor: pointer;

  &--selected {
    background: var(--v2-surface-hover, rgba(0, 0, 0, 0.04));
  }

  &--disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  input {
    width: 18px;
    height: 18px;
    accent-color: var(--v2-primary);
    flex-shrink: 0;
  }
}

.v2-unassign-step__row-main {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.v2-unassign-step__row-sub {
  font-size: 0.8125rem;
  opacity: 0.7;
}

.v2-unassign-step__select-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    accent-color: var(--v2-primary);
  }
}

.v2-unassign-step__count,
.v2-unassign-step__empty {
  font-size: 0.875rem;
  opacity: 0.7;
}
</style>
